import { FC, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Button, Icon, DropdownMenu } from '@pwc/pwc-deals-ui';
import { ErrorUtilities, TextUtilities } from '@pwc/pwc-deals-web-common';
import { debounce } from 'lodash';
import {
  getUserPreferences,
  SORT_SETTING_KEY,
} from 'common/services/userPreferencesProjectService';
import { projectsContentsSearch } from 'features/analyses/analysesService';
import { useTranslation } from 'react-i18next';
import {
  DefaultCustomUnGroupedName,
  ADD_GROUP_API_ERROR,
  GROUP_PAGE_SIZE,
} from 'features/analysesGroups/models/constants';
import {
  restrictedErrorMessage,
  isTitleRestricted,
} from 'common/helpers/titlesBlackList';

import {
  getErrorMessageDuplicateTitle,
  MatchItemsType,
} from 'common/helpers/duplicateTitleError';

import {
  getByKFA,
  getGroups,
  createAnalysesGroup,
  getAnalysis,
} from 'features/analysesGroups/services/groupsService';
import Group from 'features/analysesGroups/models/Group';
import GroupKFAInterface from 'features/analysesGroups/models/GroupKFAInterface';
import GroupedAnalysis from 'features/analysesGroups/models/GroupedAnalysis';
import { optionDetails as detailsNone } from 'features/analysesGroups/utils/useSortingDropdownNone';
import { optionDetails as detailsKfa } from 'features/analysesGroups/utils/useSortingDropdownKfa';
import { optionDetails as detailsCustom } from 'features/analysesGroups/utils/useSortingDropdownCustom';
import useGroupsPermissions from 'features/analysesGroups/utils/useGroupsPermissions';
import useGroupDropdown from 'features/analysesGroups/utils/useGroupDropdown';
import { Dropdown } from 'features/analysesGroups/models/Dropdown';
import AnalysisGroupsSorting from 'features/analysesGroups/components/AnalysisGroupsSorting/AnalysisGroupsSorting';
import GroupsPermissions from 'features/analysesGroups/components/GroupsPermissions/GroupsPermissions';
import AnalysesCustomGroupList from 'features/analysesGroups/components/AnalysesCustomGroupList/AnalysesCustomGroupList';
import AnalysesKfaGroupList from 'features/analysesGroups/components/AnalysesKfaGroupList/AnalysesKfaGroupList';
import AnalysesNoGroupingList from 'features/analysesGroups/components/AnalysesNoGroupingList/AnalysesNoGroupingList';
import Banner from 'features/analysesGroups/components/Banner/Banner';
import RadioMenu from 'features/analysesGroups/components/RadioMenu';
import EmptyGroup from 'features/analysesGroups/components/EmptyGroup/EmptyGroup';
import './AnalysisGroupsList.scss';
import {
  getProjectStatus,
  useProjectsStatuses,
} from 'common/hooks/useProjectsStatuses';
import { isProjectInactive } from 'common/helpers/projectStatusesUtils';
import { useSelector } from 'react-redux';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';

type Props = {
  projectId: number;
  isKFAsPage: boolean;
  isCustomPage: boolean;
  isNonePage: boolean;
  tags: number[];
  setShowApiError: (showApiError: boolean) => void;
};

const AnalysisGroupsList: FC<Props> = ({
  projectId,
  isCustomPage,
  isKFAsPage,
  isNonePage,
  tags,
  setShowApiError,
}) => {
  const userRole = useSelector(getUserRole);
  const [groups, setGroups] = useState<Group[]>([]);
  const [kfaGroups, setKFAs] = useState<GroupKFAInterface[]>([]);
  const [groupsLoading, setGroupsLoading] = useState<boolean>(false);
  const [kfaGroupsLoading, setKfaGroupsLoading] = useState<boolean>(false);
  const [analysisCount, setAnalysisCount] = useState<number>(-1);
  const [showAddGroupRow, setShowAddGroupRow] = useState(false);
  const [createGroupError, setCreateGroupError] = useState(false);
  const [createGroupErrorMessage, setCreateGroupErrorMessage] = useState('');
  const [noneGroupIsLoading, setNoneGroupIsLoading] = useState(false);
  const [analysesNone, setAnalysesNone] = useState<GroupedAnalysis[]>([]);
  const pageRef = useRef(0);
  const sortByRef = useRef('');
  const orderAscRef = useRef(true);
  const preferredSortIndexRef = useRef(-1);
  const isInitialMountRef = useRef(false);
  const [showSortingNone, setShowSortingNone] = useState(false);
  const [showSortingKfa, setShowSortingKfa] = useState(false);
  const [showSortingCustom, setShowSortingCustom] = useState(false);
  const [userPreference, setUserPreference] = useState(null);

  const permissions = useGroupsPermissions(projectId);

  useEffect(() => {
    setAnalysesNone([]);
  }, [userRole]);

  const displaySortingHandler = useCallback(
    (analysisGroups: Group[] | GroupKFAInterface[] = [], isKfa?: boolean) => {
      if (analysisGroups.length > 2) {
        if (isKfa) {
          setShowSortingKfa(true);
          return;
        }
        setShowSortingCustom(true);
      } else {
        if (isKfa) {
          setShowSortingKfa(false);
          return;
        }
        setShowSortingCustom(false);
      }
    },
    []
  );

  const loadGroups = useCallback(
    async (
      tagIds: number[],
      sortBy: string = detailsKfa.contentOrder.apiParameter
    ): Promise<void> => {
      try {
        if (isCustomPage) {
          if (sortBy) {
            sortByRef.current = sortBy;
          }
          setGroupsLoading(true);
          const groupsLoaded = await getGroups(
            projectId,
            tagIds,
            sortBy,
            orderAscRef.current
          );
          const groupsModified = groupsLoaded.map((grp: Group) => ({
            ...grp,
            items: [],
          }));

          const rowCount = groupsLoaded.reduce(
            (total, group) => total + (group.contentCount ?? 0),
            0
          );

          setAnalysisCount(rowCount);
          setGroups(groupsModified);
          setGroupsLoading(false);

          displaySortingHandler(groupsModified);
        }
      } catch (e) {
        setGroupsLoading(false);
        setShowApiError(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tags]
  );

  useEffect(() => {
    const loadCustomGroups = async () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      await loadGroups(tags);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
    };
    if (isCustomPage) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadCustomGroups();
    }
  }, [isCustomPage, projectId, tags, loadGroups, userRole]);

  const loadKFAGroups = useCallback(
    async (
      tagIds: number[],
      sortBy: string = detailsKfa.contentOrder.apiParameter
    ) => {
      try {
        if (sortBy) {
          sortByRef.current = sortBy;
        }
        setKfaGroupsLoading(true);
        const groupsLoaded = await getByKFA(
          projectId,
          tagIds,
          sortBy,
          orderAscRef.current
        );
        const groupsModified = groupsLoaded.map((grp) => ({
          ...grp,
          items: [],
        }));
        const rowCount = groupsLoaded.reduce(
          (total, group) => total + (group.contentCount ?? 0),
          0
        );

        setAnalysisCount(rowCount);
        setKFAs(groupsModified);
        setKfaGroupsLoading(false);

        displaySortingHandler(groupsModified, true);
      } catch (e) {
        setKfaGroupsLoading(false);
        setShowApiError(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tags]
  );

  useEffect(() => {
    const loadKfas = async () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      await loadKFAGroups(tags);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
    };
    if (isKFAsPage) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadKfas();
    }
  }, [isKFAsPage, projectId, tags, loadKFAGroups, userRole]);

  const displayAddGroupRow = () => setShowAddGroupRow(true);

  const onAddGroupCancel = () => {
    setShowAddGroupRow(false);
    setCreateGroupError(false);
  };
  const { t } = useTranslation();

  const searchTitleDuplicity = useCallback(
    async (newGroupName: string) => {
      const trimmedTitle = TextUtilities.trimText(newGroupName) as string;
      if (!trimmedTitle.length) {
        setCreateGroupError(false);
        setCreateGroupErrorMessage('');
        return false;
      }
      if (
        trimmedTitle.toLowerCase() ===
        TextUtilities.trimText(DefaultCustomUnGroupedName.toLowerCase())
      ) {
        setCreateGroupError(true);
        setCreateGroupErrorMessage(
          getErrorMessageDuplicateTitle(
            {
              contentType: 900,
              contentId: 0,
              contentSubType: 0,
              contentThreadId: 0,
              title: '',
              versionStatus: 0,
            },
            t
          )
        );
        return false;
      }

      if (isTitleRestricted(newGroupName)) {
        setCreateGroupError(true);
        setCreateGroupErrorMessage(restrictedErrorMessage);
        return false;
      }
      try {
        const matchItems = (await projectsContentsSearch(projectId, {
          title: trimmedTitle,
        })) as MatchItemsType;
        if (
          matchItems &&
          matchItems.length > 0 &&
          (
            TextUtilities.trimText(matchItems[0]?.title || '') as string
          ).toLowerCase() === trimmedTitle.toLowerCase()
        ) {
          setCreateGroupError(true);
          setCreateGroupErrorMessage(
            getErrorMessageDuplicateTitle(matchItems[0], t)
          );
          return false;
        }
        if (createGroupError) {
          setCreateGroupError(false);
          setCreateGroupErrorMessage('');
        }
      } catch (e) {
        setCreateGroupError(true);
        setCreateGroupErrorMessage(
          ErrorUtilities.getErrorMessage(e, 'Title duplicity control failed')
        );
        return false;
      }

      return true;
    },
    [createGroupError, projectId, t]
  );

  const debouncedSearchTitleDuplicity = useMemo(
    () => debounce(searchTitleDuplicity, 250),
    [searchTitleDuplicity]
  );

  const onGroupCreated = async (groupName: string) => {
    try {
      if (!groups || groups.length === 0) return; // groups should be always set at this stage, at least with one last group (ungrouped analysis group)

      const isUniqueTitle = await searchTitleDuplicity(groupName);
      if (!isUniqueTitle) {
        return;
      }

      const group = await createAnalysesGroup(Number(projectId), groupName);

      if (group) {
        const newGroup: Group = {
          projectId,
          groupId: group.id,
          title: group.name,
          contentCount: 0,
          sortOrder: 0,
          items: [],
        };

        const updatedGroups = [newGroup, ...groups].map((g, index) => {
          return { ...g, sortOrder: index + 1 };
        });

        setGroups(updatedGroups);
        setShowAddGroupRow(false);
        displaySortingHandler(updatedGroups);
      }
    } catch (e) {
      setCreateGroupError(true);
      setCreateGroupErrorMessage(ADD_GROUP_API_ERROR);
    }
  };

  const showAddGroupBtn =
    groups && groups.filter(({ groupId }) => groupId !== -1).length > 0;
  const defaultGroupBy = isKFAsPage ? 'Key Focus Area' : 'None';

  const { options, selectedOption }: Dropdown = useGroupDropdown(
    isCustomPage ? 'Custom' : defaultGroupBy
  );

  const fetchAnalysesNone = useCallback(
    async (
      tagIds: number[],
      sortBy: string = detailsNone.title.apiParameter,
      page?: number
    ) => {
      try {
        if (sortBy) {
          sortByRef.current = sortBy;
        }
        if (page !== undefined) {
          pageRef.current = page;
        }
        const { items = [], rowCount } = await getAnalysis(
          projectId,
          pageRef.current,
          GROUP_PAGE_SIZE,
          tagIds,
          sortByRef.current,
          orderAscRef.current
        );

        setAnalysisCount(rowCount);

        if (tagIds && tagIds.length > 0) {
          setAnalysesNone([...items]);
          pageRef.current = 0;
        } else if (pageRef.current === 0) {
          setAnalysesNone([...items]);
        } else {
          setAnalysesNone([...analysesNone, ...items]);
        }

        // sorting should be shown only when there are more than 1 analyses
        if (items.length > 1) {
          setShowSortingNone(true);
        } else {
          setShowSortingNone(false);
        }
      } catch (error) {
        setShowSortingNone(false);
        setShowApiError(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, pageRef, analysesNone, tags]
  );

  const getAnalysesNone = useCallback(
    async (
      tagIds: number[],
      sortBy: string = detailsNone.title.apiParameter,
      page?: number
    ) => {
      setNoneGroupIsLoading(true);
      if (page !== undefined) {
        await fetchAnalysesNone(tagIds, sortBy, page);
      } else {
        await fetchAnalysesNone(tagIds, sortBy);
      }
      setNoneGroupIsLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tags]
  );

  useEffect(() => {
    if (!isInitialMountRef.current) {
      // make sure this userEffect is not triggered on the first mounting
      isInitialMountRef.current = true;
      return;
    }
    const loadAnalyses = async () => {
      if (sortByRef.current) {
        await getAnalysesNone(tags, sortByRef.current);
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      await getAnalysesNone(tags);
    };

    if (isNonePage) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadAnalyses();
    } else {
      pageRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, userRole]);

  const isUserPreferenceValid = (
    groupUserPreferences: { settingValue: string }[]
  ) => {
    return (
      groupUserPreferences &&
      groupUserPreferences.length > 0 &&
      groupUserPreferences[0].settingValue &&
      groupUserPreferences[0].settingValue.includes('-')
    );
  };

  useEffect(() => {
    const loadUserPreferences = async () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const savedGroupUserPreferences = await getUserPreferences(
        projectId,
        SORT_SETTING_KEY
      );
      if (isUserPreferenceValid(savedGroupUserPreferences)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const preference = (
          savedGroupUserPreferences[0].settingValue as string
        ).split('-');
        const groupName = preference[0];
        if (groupName === 'none') {
          setUserPreference(savedGroupUserPreferences);
          const apiParameter = preference[1];
          sortByRef.current = apiParameter;
          if (preference.length > 2) {
            const sortingAsc = preference[2].toLowerCase() === 'asc';
            orderAscRef.current = sortingAsc;
          }
          await getAnalysesNone(tags, apiParameter, 0);
          return;
        }
      }
      await getAnalysesNone(tags);
    };
    if (isNonePage) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadUserPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const projectsStates = useProjectsStatuses();
  const projectStatus = getProjectStatus(projectsStates, projectId);
  const isInactive = isProjectInactive(projectStatus);

  return (
    <>
      <div className="analysis-groups-list">
        <div className="radio-menu-wrapper">
          <RadioMenu
            defaultGroupBy={isCustomPage ? 'Custom' : defaultGroupBy}
          />
        </div>
        <div className="dropdown-menu-wrapper">
          {options.length > 2 && (
            <DropdownMenu title={selectedOption} items={options} />
          )}
        </div>

        {(showSortingCustom || showSortingKfa || showSortingNone) && (
          <div className="analysis-groups-list__separator" />
        )}

        {showSortingNone && (
          <AnalysisGroupsSorting
            projectId={projectId}
            orderAscRef={orderAscRef}
            tags={tags}
            sortBy={sortByRef.current}
            pageRef={pageRef}
            sortingType="none"
            sortingCallback={getAnalysesNone}
            permissions={permissions}
            userPreference={userPreference}
            preferredSortIndexRef={preferredSortIndexRef}
          />
        )}

        <GroupsPermissions projectId={projectId}>
          <>
            {showAddGroupBtn && (
              <div className="add-button-wrapper">
                <Button
                  className="button-menu"
                  onClick={displayAddGroupRow}
                  iconClasses=""
                  icon=""
                >
                  <Icon
                    name="circle-plus"
                    width="21"
                    className="outline icon-orange"
                  />
                  {t('addGroupBtn')}
                </Button>
              </div>
            )}
          </>
        </GroupsPermissions>
      </div>
      {!groupsLoading && !kfaGroupsLoading && !noneGroupIsLoading && (
        <GroupsPermissions projectId={projectId}>
          <>
            {!showAddGroupRow &&
              (groups.length === 1 || kfaGroups.length === 1 || isNonePage) && (
                <Banner
                  projectId={projectId}
                  noAnalysis={analysisCount === 0}
                  isCustomPage={isCustomPage}
                  isKFAsPage={isKFAsPage}
                  isNonePage={isNonePage}
                  displayAddGroupRow={displayAddGroupRow}
                  groups={groups}
                  kfaGroups={kfaGroups}
                  analysisCount={analysisCount}
                  tags={tags}
                />
              )}

            {showAddGroupRow && (
              <EmptyGroup
                num={groups ? 1 : 0}
                createGroupError={createGroupError}
                createGroupErrorMessage={createGroupErrorMessage}
                handleCancelAddGroup={onAddGroupCancel}
                handleAddGroup={onGroupCreated}
                handleChangeGroupName={debouncedSearchTitleDuplicity}
              />
            )}
          </>
        </GroupsPermissions>
      )}
      {isCustomPage && (
        <AnalysesCustomGroupList
          groups={groups}
          analysisCount={analysisCount}
          setAnalysisCount={setAnalysisCount}
          setGroups={setGroups}
          groupsLoading={groupsLoading}
          tags={tags}
          isAddingNewGroup={showAddGroupRow}
          permissions={permissions}
          enableDragAndDrop={
            sortByRef.current === detailsCustom.contentOrder.apiParameter &&
            !isInactive
          }
          displaySortingHandler={displaySortingHandler}
        />
      )}
      {isKFAsPage && (
        <AnalysesKfaGroupList
          groups={kfaGroups}
          analysisCount={analysisCount}
          setAnalysisCount={setAnalysisCount}
          setGroups={setKFAs}
          groupsLoading={kfaGroupsLoading}
          tags={tags}
          permissions={permissions}
        />
      )}
      {isNonePage && (
        <AnalysesNoGroupingList
          projectId={projectId}
          tags={tags}
          analysisCount={analysisCount}
          setAnalysisCount={setAnalysisCount}
          isLoading={noneGroupIsLoading}
          analyses={analysesNone}
          setAnalyses={setAnalysesNone}
          permissions={permissions}
          fetchAnalyses={fetchAnalysesNone}
          pageRef={pageRef}
          sortByRef={sortByRef}
        />
      )}
    </>
  );
};

export default AnalysisGroupsList;
